import React, {useEffect, useMemo, useState} from 'react';
import {useTalentTeam} from "../../../providers/TalentTeamProvider";
import {useAuth} from "../../../providers/AuthProvider";
import {Empty, Input, Select, Table as AntdTable} from "antd";
import InviteTeamMemberModal from "./InviteTeamMemberModal";
import AddTeamMemberModal from "../../suppliers/components/AddTeamMemberModal";
import AdminAddTeamMemberModal from "./AdminAddTeamMemberModal";
import UserImageCircle from "../../../components/UserImageCircle";
import SortableTable from "../../../components/SortableTable"
import InfoTooltip from "../../../components/InfoTooltip";
import SpinLoader from "../../../components/SpinLoader";

const TeamMemberTable = ({split = true, ...props}) => {

    const {
        team,
        isAuthAdmin,
        isAuthOwner,
        cancelInvite,
        resendInvite,
        isUpdating,
        revokeTeamMemberAdminRights,
        grantTeamMemberAdminRights,
        updateTeamMembers,
        removeTeamMember
    } = useTalentTeam();
    const {auth} = useAuth();

    const [editingKey, setEditingKey] = useState(null);


    /**
     * Pending-invite rows come back with a null `id`, and `editingKey` starts as
     * null — so a bare `record.id === editingKey` made every invite row render in
     * edit mode at once (duplicate Cancel buttons, a stray title input). Require
     * an active key.
     */
    const isEditing = (record) => {
        return editingKey !== null && record.id === editingKey
    }
    /**
     * Rows are a mix of real team positions and pending-invite placeholders (which
     * come back with a null `id`), so neither field is unique on its own. This
     * gives every row a stable, collision-free key.
     */
    const getRowKey = (member) => (
        member.id ? `position-${member.id}` : `invite-${member.invite_id}`
    );

    /** Restore the table to whatever the server last told us the order was. */
    const resetSortedMembers = () => {
        const [joinedMembers] = TB.partition(positions, ['has_joined', true]);

        setSortedMembers(TB.orderBy([...(split ? joinedMembers : positions)], 'display_index'));
    };

    const Table = team.can_manage ? SortableTable : AntdTable;
    const tableProps = team.can_manage ? {
        onSortChange: async (source) => {
            // Re-index against the full visible list so what the user sees is what
            // gets saved. Indexes are 0-based to match the server; invite rows are
            // filtered out downstream, and the server closes the resulting gaps.
            const reordered = TB.map([...source], (member, dIndex) => ({
                ...member,
                display_index: dIndex
            }));

            await setSortedMembers(reordered);

            // Nothing to persist if the roster is invites only.
            if (!TB.some(reordered, member => !!member.id)) {
                return;
            }

            const success = await updateTeamMembers(reordered);
            if (!success) {
                resetSortedMembers();
            }
        },
        canSort: !editingKey,
        sortLabel: (
            <span className="flex flex-col items-center text-center text-xs">
                <span className="whitespace-no-wrap">
                    Order Of
                </span>
                <span className="flex flex-row items-center justify-center">
                    <span>Display</span>
                    <InfoTooltip type="dark" placement="top" iconColor="black" iconSize={12} iconStyle={{marginLeft: 3}}>
                        Order in which talent’s image is shown when the team is presented to potential clients
                    </InfoTooltip>
                </span>
            </span>
        ),
        rowClassName: () => 'editable-row bg-white'
    } : {};


    const positions = useMemo(() => {
        let members = team?.positions || [];
        if (props.hideAuth) {
            members = TB.filter(members, pos => pos.user?.id !== auth.id)
        }
        return members
        // Deliberately not keyed on `isUpdating`: recomputing mid-request gave this
        // array a new identity and made the resync effect below revert an
        // optimistic re-order before the server had even replied.
    }, [team?.positions, props.hideAuth, auth.id]);

    const isSupplier = auth.canManageTalent() ? team.is_supplied_by_client : auth.canManageClientResourceTalent();

    const [members, invites] = TB.partition(positions, ['has_joined', true]);
    const [sortedMembers, setSortedMembers] = useState(TB.orderBy([...split ? members : positions], 'display_index'));

    // `sortedMembers` is local state so rows can be re-ordered and edited before
    // saving, which means it only ever held the roster as it looked on first
    // render. Re-seed it whenever the team roster actually changes on the server
    // (member added, removed, invite cancelled) so the table stays in sync.
    //
    // Depends on `positions` rather than `members` because `members` comes out of
    // TB.partition and gets a fresh identity every render, which would loop.
    useEffect(() => {
        // Never re-seed mid-edit, or we'd throw away the user's unsaved title.
        // `editingKey` is a dependency so the roster resyncs as soon as the edit
        // is saved or cancelled.
        if (editingKey) return;

        resetSortedMembers();
    }, [positions, split, editingKey]);

    return (
        <div className="overflow-hidden">
            {positions?.length > 0 ? (
                <React.Fragment>
                    <div className="table-responsive team-member-table">
                        <SpinLoader isLoading={isUpdating}>
                            <Table
                                {...tableProps}
                                bordered
                                rowKey={getRowKey}
                                pagination={false}
                                dataSource={sortedMembers}
                                className={TB.mapClass('bg-white', {'mb-0': !team.can_manage})}
                            >

                                <AntdTable.Column
                                    title="Name/Email"
                                    dataIndex="role"
                                    render={(_, member) => {
                                        let canEditRole = isAuthOwner &&
                                            member.user?.id !== auth.id &&
                                            member.role !== 'Owner' &&
                                            member.user?.is_active;

                                        return (
                                            <div className="flex flex-row items-center gap-3">
                                                <div className="text-center flex flex-col gap-2 items-center">
                                                    <UserImageCircle size={55}
                                                                     user={member.user?.first_name ? member.user : {label: member.user?.email}}
                                                                     className="mx-auto img-grayscale"/>
                                                </div>
                                                <div className="flex flex-col items-start">
                                                    {member.user?.first_name && (
                                                        <p className="text-sm font-semibold select-none mb-2">
                                                            {member.user?.fullName?.()}
                                                        </p>
                                                    )}
                                                    <a href={`mailto:${member.user ? member.user?.email : member.email}`}
                                                       target="_blank"
                                                       className="text-xs btn-link-info">
                                                        {member.user ? member.user?.email : member.email}
                                                    </a>
                                                    {!isSupplier && (
                                                        canEditRole ? (
                                                            <Select
                                                                defaultValue={member.role}
                                                                size="small"
                                                                style={{width: 90}}
                                                                className="text-xs font-medium mt-2"
                                                                loading={isUpdating}
                                                                onChange={async (value) => {

                                                                    let success;
                                                                    if (value === 'Admin' && member.role === 'Member') {
                                                                        success = await grantTeamMemberAdminRights(member)
                                                                    } else if (value === 'Member' && member.role === 'Admin') {
                                                                        success = await revokeTeamMemberAdminRights(member)
                                                                    }
                                                                    if (success) {
                                                                        let updatedPositions = success?.positions || [];
                                                                        if (props.hideAuth) {
                                                                            updatedPositions = TB.filter(updatedPositions, pos => pos.user?.id !== auth.id)
                                                                        }
                                                                        const [updatedMembers, invites] = TB.partition(updatedPositions, ['has_joined', true]);

                                                                        await setSortedMembers(TB.orderBy(split ? updatedMembers : updatedPositions, 'display_index'))

                                                                    }
                                                                }}
                                                                disabled={isUpdating || !team.can_manage || member.role === 'Owner' || (member.role === 'Admin' && isAuthAdmin) || editingKey}
                                                                options={TB.reduce(['Owner', 'Admin', 'Member'], (result, value) => {
                                                                    if (value === 'Owner' && member.role !== 'Owner') {
                                                                        return result;
                                                                    }
                                                                    result.push({
                                                                        label: value,
                                                                        value,
                                                                        disabled: value === 'Owner' || (isAuthAdmin && value === 'Member')
                                                                    })

                                                                    return result;
                                                                }, [])}
                                                            />
                                                        ) : <p className="text-xs font-medium mt-2">
                                                            {(member.role || 'Member')}
                                                        </p>
                                                    )}
                                                </div>

                                            </div>
                                        )
                                    }}
                                />
                                <AntdTable.Column
                                    title="Roles"
                                    dataIndex="disciplines"
                                    render={(_, member) => {
                                        const disciplines = TB.map(member.user?.disciplines || [], 'discipline.name');
                                        return (
                                            <div className="flex flex-col items-center justify-center w-[210px]">
                                                {!member.has_joined ? (
                                                    <p className="text-center text-xs italic text-gray">
                                                        {(member.invited_by && !member.was_rejected) ? 'Pending Invite' : 'N/A'}
                                                    </p>
                                                ) : (
                                                    <React.Fragment>
                                                        <p className="text-center text-xs select-none">
                                                            {disciplines.length > 0 && TB.join(TB.take(disciplines, 5), ', ')}
                                                            {disciplines.length > 5 && (
                                                                <React.Fragment>
                                                                <span id={`more-roles-${member.id}`}
                                                                      className="text-info font-semibold italic pr-2 whitespace-no-wrap"> ...+{disciplines.length - 5} more
                                                                </span>
                                                                    <InfoTooltip type="dark" placement="right"
                                                                                 target={`more-roles-${member.id}`}>
                                                                    <span className="text-white p-2">
                                                                        {TB.join(TB.drop(disciplines, 5), ', ')}
                                                                    </span>
                                                                    </InfoTooltip>
                                                                </React.Fragment>
                                                            )}
                                                        </p>
                                                    </React.Fragment>
                                                )}

                                            </div>
                                        )
                                    }}
                                />
                                {/*{!isSupplier && (*/}
                                {/*    <AntdTable.Column*/}
                                {/*        title="Source"*/}
                                {/*        dataIndex="invited_by"*/}
                                {/*        render={(_, member) => (*/}
                                {/*            <div>*/}
                                {/*                <p className="whitespace-nowrap">*/}
                                {/*                    {*/}
                                {/*                        member.invited_by ? (*/}
                                {/*                            member.invited_by?.id === auth.id ? (*/}
                                {/*                                <i>Invited by Me</i>*/}
                                {/*                            ) : (*/}
                                {/*                                `${member.invited_by?.first_name} ${member.invited_by?.last_name}`*/}
                                {/*                            )*/}
                                {/*                        ) : auth.id === member.user.id ? (*/}
                                {/*                            <span className="text-danger"><i>(You)</i></span>*/}
                                {/*                        ) : member.role === 'Owner' && 'Owner'*/}
                                {/*                    }*/}
                                {/*                </p>*/}
                                {/*            </div>*/}
                                {/*        )}*/}
                                {/*    />*/}
                                {/*)}*/}
                                {/*{!isSupplier && (*/}
                                <AntdTable.Column
                                    title="Team Position Title"
                                    dataIndex="role"
                                    render={(_, member, mIndex) => {
                                        let canEditRole = isAuthOwner;

                                        return (
                                            <div>
                                                {(canEditRole && isEditing(member) ? (
                                                    <Input.TextArea
                                                        autoFocus={true}
                                                        value={member.title}
                                                        size="small"
                                                        showCount maxLength={100}
                                                        className="text-xs editable-cell-input"
                                                        autoSize
                                                        onChange={(e) => {

                                                            let updatedMembers = [...sortedMembers];
                                                            updatedMembers[mIndex] = {
                                                                ...updatedMembers[mIndex],
                                                                title: e.target.value
                                                            };

                                                            console.log('updatedMembers', updatedMembers.length);

                                                            setSortedMembers(updatedMembers)
                                                        }}
                                                    />
                                                ) : (
                                                    <p className={TB.mapClass("text-xs", {
                                                        "editable-cell-value-wrap": (canEditRole && !editingKey),
                                                        "noneditable-cell-value-wrap": !canEditRole || editingKey,
                                                    })}
                                                       onClick={(e) => {
                                                           if (canEditRole && !editingKey) setEditingKey(member.id);
                                                       }}>{member.title}</p>
                                                ))}
                                            </div>
                                        )
                                    }}
                                />
                                {/*)}*/}
                                <AntdTable.Column
                                    title="Status"
                                    dataIndex="has_joined"
                                    render={(_, member) => (
                                        <div className="flex justify-center text-xs">

                                            {!member.has_joined ? (
                                                !member.was_rejected ? (
                                                    <>
                                                        <span>Pending</span>
                                                        <div className="ml-2 h-3.5 w-3.5 bg-warning rounded-full"/>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span>Declined</span>
                                                        <div className="ml-2 h-3.5 w-3.5 bg-gray rounded-full"/>
                                                    </>
                                                )
                                            ) : (
                                                !member.user?.is_active ? (
                                                    <>
                                                        <span>Inactive</span>
                                                        <div className="ml-2 h-3.5 w-3.5 bg-danger rounded-full"/>
                                                    </>
                                                ) : (
                                                    !member.user?.is_available ? (
                                                        <>
                                                            <span>Unavailable</span>
                                                            <div
                                                                className="ml-2 h-3.5 w-3.5 bg-danger rounded-full"/>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span>Active</span>
                                                            <div
                                                                className="ml-2 h-3.5 w-3.5 bg-green-300 rounded-full"/>
                                                        </>
                                                    )
                                                )
                                            )}
                                        </div>
                                    )}
                                />
                                {team.can_manage && (
                                    <AntdTable.Column
                                        title="Actions"
                                        width={70}
                                        dataIndex="actions"
                                        render={(_, member) => {
                                            const isMemberAuth = member.user?.id === auth.id;

                                            return (
                                                <div>
                                                    <div className="flex flex-col gap-1 items-center justify-center">
                                                        {isEditing(member) ? (
                                                            <React.Fragment>
                                                                <button
                                                                    className="min-w-[unset] btn btn-link btn-link-danger p-0 whitespace-nowrap"
                                                                    disabled={isUpdating}
                                                                    onClick={async () => {
                                                                        resetSortedMembers()
                                                                        await setEditingKey(null)
                                                                    }}>
                                                                    Cancel
                                                                </button>
                                                                <button
                                                                    className="min-w-[unset] btn btn-link btn-link-info p-0 whitespace-nowrap"
                                                                    disabled={isUpdating}
                                                                    onClick={async () => {
                                                                        let success = await updateTeamMembers(sortedMembers);
                                                                        if (success) {
                                                                            await setEditingKey(null)
                                                                        }
                                                                    }}>
                                                                    Save
                                                                </button>
                                                            </React.Fragment>
                                                        ) : (isSupplier || (member.has_joined && !isMemberAuth && (isAuthOwner || (isAuthAdmin && member.role === 'Member')))) && (
                                                            <React.Fragment>
                                                                <button
                                                                    className="min-w-[unset] btn btn-link btn-link-danger p-0 whitespace-nowrap"
                                                                    disabled={isUpdating || !!editingKey}
                                                                    onClick={() => removeTeamMember(member)}>
                                                                    Remove
                                                                </button>
                                                                {/*<button*/}
                                                                {/*    className="min-w-[unset] btn btn-link btn-link-warning p-0 whitespace-nowrap"*/}
                                                                {/*    disabled={isUpdating || !!editingKey}*/}
                                                                {/*    onClick={() => setEditingKey(member.id)}>*/}
                                                                {/*    Edit*/}
                                                                {/*</button>*/}
                                                            </React.Fragment>
                                                        )}
                                                        {!isSupplier && (!TB.isDefined(member.is_active) || member.is_active) && !member.has_joined && !isMemberAuth && (isAuthOwner || isAuthAdmin) && (
                                                            <React.Fragment>
                                                                {!TB.moment(member.updated_at).isSame(member.created_at) && !TB.moment().isSame(member.updated_at, 'day') && (
                                                                    <button
                                                                        className="min-w-[unset] btn btn-link btn-link-primary p-0 whitespace-nowrap"
                                                                        disabled={isUpdating}
                                                                        onClick={() => resendInvite(member)}>
                                                                        Resend Invite
                                                                    </button>
                                                                )}
                                                                <button
                                                                    className="min-w-[unset] btn btn-link btn-link-danger p-0 whitespace-nowrap"
                                                                    disabled={isUpdating}
                                                                    onClick={() => cancelInvite(member)}>
                                                                    Cancel
                                                                </button>
                                                            </React.Fragment>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        }}
                                    />
                                )}
                            </Table>
                        </SpinLoader>

                        {/*<table*/}
                        {/*    className={TB.mapClass('table table-auto table-bordered bg-white', {'mb-0': !team.can_manage})}>*/}
                        {/*    <thead>*/}
                        {/*    <tr className="uppercase">*/}
                        {/*        <th className=""></th>*/}
                        {/*        <th className="px-6 py-4">Name/Email</th>*/}
                        {/*        <th className="px-6 py-4">Roles</th>*/}
                        {/*        {!isSupplier && (<th className="px-6 py-4">Source</th>)}*/}
                        {/*        <th className="px-6 py-4">Type</th>*/}
                        {/*        {!isSupplier && (<th className="px-6 py-4">Status</th>)}*/}
                        {/*        {team.can_manage && (<th className="px-6 py-4"></th>)}*/}
                        {/*    </tr>*/}
                        {/*    </thead>*/}
                        {/*    <tbody>*/}
                        {/*    {TB.map(split ? members : positions, (pos, ind) => {*/}
                        {/*        if (pos.was_rejected) {*/}
                        {/*            return null;*/}
                        {/*        }*/}
                        {/*        return (*/}
                        {/*            <TeamMemberRow isSupplier={isSupplier} member={pos} display={ind + 1}*/}
                        {/*                           key={`position-${pos.id || pos.invite_id}`}/>*/}
                        {/*        )*/}
                        {/*    })}*/}

                        {/*    </tbody>*/}
                        {/*</table>*/}
                    </div>
                    {!props.hideInvite && (
                        <div className="my-4 flex flex-row items-center gap-3">
                            {isSupplier ? <AddTeamMemberModal/> :
                                (team.can_manage && <InviteTeamMemberModal/>)}
                            {/* Talent managers get a direct-add option next to the
                                invite flow. Renders null for everyone else. */}
                            <AdminAddTeamMemberModal/>
                        </div>
                    )}

                    {!isSupplier && split && invites.length > 0 && (
                        <React.Fragment>
                            <div className="mt-6 px-6 py-4 mb-4 bg-[#C2CFE0]">
                                <p className="font-bold">Team Member Invitations</p>
                            </div>
                            <div className="table-responsive team-member-table">

                                <table className="table table-auto table-bordered bg-white">

                                    <thead>
                                    <tr className="uppercase">
                                        <th className=""></th>
                                        <th className="px-6 py-4">Name/Email</th>
                                        <th className="px-6 py-4">Invite Sent</th>
                                        <th className="px-6 py-4">Source</th>
                                        <th className="px-6 py-4">Status</th>
                                        {team.can_manage && (<th className="px-6 py-4"></th>)}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {TB.map(invites, (pos, ind) => {
                                        const isMemberAuth = pos.user.id === auth.id
                                        return (
                                            <tr key={`invite-${pos.user.id || pos.user.email}`}
                                                className={TB.mapClass("text-center text-xs", {
                                                    "bg-[#F5F6F8]": pos.was_rejected
                                                })}>
                                                <td className="text-center">
                                                    <UserImageCircle size={40}
                                                                     user={pos.user?.first_name ? pos.user : {label: pos.user?.email}}
                                                                     className="mx-auto img-grayscale"/>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {pos.user?.first_name && (
                                                        <p className="text-center text-xs font-semibold underline select-none mb-2">
                                                            {pos.user.fullName()}
                                                        </p>
                                                    )}
                                                    <a href={`mailto:${pos.user ? pos.user.email : pos.email}`}
                                                       target="_blank"
                                                       className="text-center text-xs btn-link-primary">
                                                        {pos.user ? pos.user.email : pos.email}
                                                    </a>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {TB.moment(pos.invited_at).format("lll")}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <p className="whitespace-nowrap">
                                                        {
                                                            pos.invited_by ? (
                                                                pos.invited_by?.id === auth.id ? (
                                                                    <i>Invited by Me</i>
                                                                ) : (
                                                                    `${pos.invited_by?.first_name} ${pos.invited_by?.last_name}`
                                                                )
                                                            ) : auth.id === pos.user.id ? (
                                                                <span className="text-danger"><i>(You)</i></span>
                                                            ) : pos.role === 'Owner' && 'Owner'
                                                        }

                                                    </p>
                                                </td>

                                                <td className="px-6 py-4">
                                                    <div className="flex justify-center">
                                                        {!pos.was_rejected ? (
                                                            <>
                                                                <span>Pending</span>
                                                                <div
                                                                    className="ml-2 h-3.5 w-3.5 bg-warning rounded-full"/>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span>Declined</span>
                                                                <div
                                                                    className="ml-2 h-3.5 w-3.5 bg-danger opacity-80 rounded-full"/>
                                                            </>
                                                        )}
                                                    </div>

                                                </td>
                                                {(auth.canManageTalent() || team.can_manage || isAuthOwner || isAuthAdmin) && (
                                                    <td className="px-2 py-4">
                                                        <div
                                                            className="flex flex-col gap-1 items-center justify-center">
                                                            {!pos.was_rejected ? (
                                                                <React.Fragment>
                                                                    {!TB.moment().isSame(pos.invited_at, 'hour') && (
                                                                        <button
                                                                            className="btn btn-link btn-link-primary p-0 whitespace-nowrap"
                                                                            disabled={isUpdating}
                                                                            onClick={() => resendInvite(pos)}>
                                                                            Resend Invite
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        className="btn btn-link btn-link-danger p-0 whitespace-nowrap"
                                                                        disabled={isUpdating}
                                                                        onClick={() => cancelInvite(pos)}>
                                                                        Cancel
                                                                    </button>
                                                                </React.Fragment>
                                                            ) : (
                                                                <button
                                                                    className="btn btn-link btn-link-dark p-0 whitespace-nowrap"
                                                                    disabled={isUpdating}
                                                                    onClick={() => cancelInvite(pos)}>
                                                                    Delete
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        )
                                    })}

                                    </tbody>
                                </table>
                            </div>
                        </React.Fragment>
                    )}
                </React.Fragment>
            ) : (
                <div className="w-full bg-white flex flex-col p-5 items-center justify-center min-h-[275px]">
                    <Empty description="No members invited yet" image={Empty.PRESENTED_IMAGE_SIMPLE}>
                        {!props.hideInvite && (
                            <div className="flex flex-col items-center gap-3">

                                {auth.canManageClientResourceTalent() ?
                                    <AddTeamMemberModal/> :
                                    <InviteTeamMemberModal/>
                                }
                                {/* Talent managers get a direct-add option next to the
                                    invite flow. Renders null for everyone else. */}
                                <AdminAddTeamMemberModal/>
                            </div>
                        )}
                    </Empty>
                </div>
            )}

            {}
        </div>

    );
};

export default TeamMemberTable;